import React, { forwardRef } from 'react';

const W = 794;
const H = 1123;

// Ultra-Premium Typography
const FONTS = {
  sans: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: '"Playfair Display", "Didot", "Georgia", serif'
};

const COND_LABELS = {
  acne: 'Acne & Blemishes',
  pigmentation: 'Hyperpigmentation',
  pores: 'Enlarged Pores',
  wrinkles: 'Fine Lines',
  darkCircles: 'Dark Circles',
  eyeWrinkles: "Crow's Feet",
  eyeBags: 'Under-Eye Bags',
  deepWrinkles: 'Deep Wrinkles',
  skinTexture: 'Skin Texture',
  hydrationLevel: 'Hydration Level'
};

const getSeverity = (s) => {
  const x = (s || '').toLowerCase();
  if (x === 'low') return { label: 'Mild', color: '#065F46', dot: '#10B981', bg: '#ECFDF5' };
  if (x === 'high') return { label: 'Severe', color: '#991B1B', dot: '#B91C1C', bg: '#FEF2F2' };
  return { label: 'Moderate', color: '#92400E', dot: '#F59E0B', bg: '#FFFBEB' };
};

function clampText(value, maxChars) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const safe = slice.includes(' ') ? slice.slice(0, slice.lastIndexOf(' ')) : slice;
  return `${safe.trim()}…`;
}

// --- Micro-Components ---

const Logo = () => (
  <img
    src="/assets/LOGO.svg"
    alt="my-skin.ai"
    style={{ height: 44 }}
  />
);

const SectionKicker = ({ title, subtitle, marginBottom = 32 }) => (
  <div style={{ marginBottom }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: subtitle ? 6 : 0 }}>
      <div style={{ fontFamily: FONTS.sans, fontSize: 10, fontWeight: 600, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 2, marginRight: 16 }}>
        {title}
      </div>
      <div style={{ flex: 1, height: 1, backgroundColor: '#E5E5E5' }} />
    </div>
    {subtitle && (
      <div style={{ fontFamily: FONTS.serif, fontSize: 14, color: '#737373' }}>
        {subtitle}
      </div>
    )}
  </div>
);

function getAdaptivePageOneLayout(summaryText, concernCount) {
  const length = String(summaryText || '').length;
  const concerns = Number(concernCount) || 0;

  if (length > 300 || concerns >= 3) {
    return {
      topSectionMarginBottom: 44,
      kickerMarginBottom: 20,
      summarySectionMarginBottom: 24,
      summaryFontSize: 13,
      summaryLineHeight: '1.38em',
      summaryMaxChars: 320,
      severityFontSize: 40,
      concernLabelMarginBottom: 4,
      concernChipMarginBottom: 4,
      concernChipPadding: '4px 10px',
      concernChipFontSize: 9,
      maxConcerns: 2
    };
  }

  if (length > 220 || concerns >= 2) {
    return {
      topSectionMarginBottom: 52,
      kickerMarginBottom: 24,
      summarySectionMarginBottom: 32,
      summaryFontSize: 15,
      summaryLineHeight: '1.48em',
      summaryMaxChars: 380,
      severityFontSize: 44,
      concernLabelMarginBottom: 8,
      concernChipMarginBottom: 8,
      concernChipPadding: '6px 13px',
      concernChipFontSize: 10,
      maxConcerns: 3
    };
  }

  return {
    topSectionMarginBottom: 64,
    kickerMarginBottom: 32,
    summarySectionMarginBottom: 48,
    summaryFontSize: 17,
    summaryLineHeight: '1.6em',
    summaryMaxChars: 480,
    severityFontSize: 48,
    concernLabelMarginBottom: 8,
    concernChipMarginBottom: 8,
    concernChipPadding: '6px 14px',
    concernChipFontSize: 11,
    maxConcerns: 4
  };
}

const ReportHeader = ({ date, reportId }) => (
  <div style={{
    height: 120,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E5E5E5',
    marginBottom: 40,
    paddingBottom: 10
  }}>
    <Logo />
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
        Confidential Report
      </div>
      <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A', marginBottom: 6 }}>{reportId}</div>
      <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: '#737373', letterSpacing: 0.5 }}>{date}</div>
    </div>
  </div>
);

const ReportFooter = ({ page }) => (
  <div style={{
    height: 80,
    flexShrink: 0,
    marginTop: 'auto',
    borderTop: '1px solid #E5E5E5',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: FONTS.sans,
    fontSize: 10,
    color: '#8A8A8A',
    letterSpacing: 1,
    textTransform: 'uppercase'
  }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ color: '#1A1A1A', fontWeight: 500, marginRight: 24 }}>my-skin.ai</span>
      <span style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#D4D4D4', marginRight: 24 }} />
      <span>AI Skin Analysis</span>
    </div>
    <div style={{ fontWeight: 500 }}>PAGE {page} / 3</div>
  </div>
);

const PageFrame = ({ page, reportId, date, children }) => (
  <div
    style={{
      width: W, height: H, backgroundColor: '#FAFAF9', padding: '48px', boxSizing: 'border-box'
    }}
    data-pdf-page={String(page)}
  >
    <div style={{
      width: '100%', height: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5',
      padding: '0 48px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column'
    }}>
      <ReportHeader reportId={reportId} date={date} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {children}
      </div>
      <ReportFooter page={page} />
    </div>
  </div>
);

const ConditionBar = ({ label, percentage, severity }) => {
  const score = Math.max(0, Math.min(100, Number(percentage) || 0));
  const sev = getSeverity(severity);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 500, color: '#1A1A1A', letterSpacing: 0.2 }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: sev.color, textTransform: 'uppercase', letterSpacing: 1, marginRight: 12 }}>{sev.label}</span>
          <span style={{ fontFamily: FONTS.serif, fontSize: 16, fontWeight: 400, color: '#1A1A1A', minWidth: 36, textAlign: 'right' }}>{score}%</span>
        </div>
      </div>
      <div style={{ width: '100%', height: 2, backgroundColor: '#F0F0F0', position: 'relative' }}>
        <div style={{ width: `${score}%`, height: '100%', backgroundColor: sev.dot, position: 'absolute', top: 0, left: 0 }} />
      </div>
    </div>
  );
};

const RoutineStep = ({ step, index }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 20 }}>
    <div style={{ fontFamily: FONTS.sans, fontSize: 10, fontWeight: 500, color: '#A3A3A3', borderBottom: '1px solid #A3A3A3', paddingBottom: 2, marginTop: 2, marginRight: 16 }}>
      0{index + 1}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: FONTS.serif, fontSize: 15, color: '#1A1A1A', lineHeight: '1.5em' }}>{step || '—'}</div>
    </div>
  </div>
);

const RoutineColumn = ({ title, steps }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
    <div style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 28, paddingBottom: 12, borderBottom: '1px solid #1A1A1A' }}>
      {title}
    </div>
    <div style={{ flex: 1 }}>
      {steps.map((step, i) => <RoutineStep key={`${title}-${i}`} index={i} step={step} />)}
    </div>
  </div>
);

// --- Main Component ---

const PDFPreview = forwardRef(({ analysisData, patientData, capturedImage, annotatedImage, doctorInfo }, ref) => {
  if (!analysisData || !patientData) return <div ref={ref} />;

  const d = new Date();
  const date = `${d.getDate()} ${['January','February','March','April','May','June','July','August','September','October','November','December'][d.getMonth()]} ${d.getFullYear()}`;
  const reportId = `MSA-${String(Date.now()).slice(-6)}`;

  const conds = Object.entries(analysisData?.detailedAnalysis || {})
    .filter(([k]) => COND_LABELS[k])
    .sort((a, b) => (b[1].percentage || 0) - (a[1].percentage || 0));

  const top = conds[0];
  const overall = getSeverity(analysisData?.overallAssessment?.severity);
  const age = patientData?.age || 25;
  const summaryFallback = `The analysis indicates ${patientData.name}'s primary skin concern is ${COND_LABELS[top?.[0]] || 'general skin health'}, contributing significantly to the overall severity score. Recommendations focus on barrier repair and active treatment.`;
  const rawSummaryText = analysisData?.summary || summaryFallback;
  const rawNotedConcerns = (analysisData?.overallAssessment?.primaryConcerns || [])
    .map(c => clampText(c, 30));
  const pageOneLayout = getAdaptivePageOneLayout(rawSummaryText, rawNotedConcerns.length);
  const summaryText = clampText(rawSummaryText, pageOneLayout.summaryMaxChars);
  const notedConcerns = rawNotedConcerns.slice(0, pageOneLayout.maxConcerns);

  const skincare = analysisData?.recommendations?.skincare || '';
  const getSteps = (regex, fb) => {
    const m = skincare.match(regex);
    if (m) return m[1].split(/[,;]/).map(s => s.trim()).filter(s => s.length > 2).slice(0, 4);
    return fb;
  };
  const am = getSteps(/morning[:\s]*([^.]+)/i, ['Gentle Cleanser', 'Vitamin C Serum', 'Hydrating Moisturizer', 'Broad Spectrum SPF']);
  const pm = getSteps(/evening[:\s]*([^.]+)/i, ['Double Cleanse', 'Active Treatment', 'Repair Cream', 'Spot Treatment']);

  while (am.length < 4) am.push('');
  while (pm.length < 4) pm.push('');

  return (
    <div ref={ref} data-pdf-root style={{ width: W, height: H * 3, backgroundColor: '#D4D4D4' }}>

      {/* ============ PAGE 1: EXECUTIVE SUMMARY ============ */}
      <PageFrame page={1} reportId={reportId} date={date}>
        
        <div style={{ marginBottom: 48, maxWidth: 500 }}>
          <div style={{ fontFamily: FONTS.serif, fontSize: 46, color: '#1A1A1A', fontWeight: 400, marginBottom: 16, lineHeight: '1.1em' }}>
            Dermatological Assessment
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: '#737373', letterSpacing: 0.5, lineHeight: '1.6em' }}>
            A comprehensive, AI-assisted analysis of skin health, texture, and underlying conditions.
          </div>
        </div>

        <div style={{ display: 'flex', marginBottom: pageOneLayout.topSectionMarginBottom }}>
          <div style={{ width: 200, height: 260, flexShrink: 0, padding: 8, border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF', marginRight: 48, display: 'flex', flexDirection: 'column' }}>
            <div style={{ 
              flex: 1, 
              backgroundColor: '#F5F5F5', 
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {capturedImage?.dataUrl ? (
                <img
                  src={capturedImage.dataUrl}
                  alt="Captured skin scan"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    flexShrink: 0,
                    filter: 'contrast(1.05) grayscale(0.1)'
                  }}
                  crossOrigin="anonymous"
                />
              ) : null}
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontFamily: FONTS.serif, fontSize: 36, color: '#1A1A1A', fontWeight: 400, marginBottom: 32, borderBottom: '1px solid #E5E5E5', paddingBottom: 24 }}>
              {patientData.name.slice(0, 30)}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ width: '50%', marginBottom: 28, boxSizing: 'border-box', paddingRight: 24 }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>Age</div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A' }}>{age} Years</div>
              </div>
              <div style={{ width: '50%', marginBottom: 28, boxSizing: 'border-box', paddingLeft: 24 }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>Skin Type</div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A' }}>{analysisData.patientAnalysis?.skinType || 'Combination'}</div>
              </div>
              <div style={{ width: '50%', boxSizing: 'border-box', paddingRight: 24 }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>Primary Concern</div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A' }}>{patientData.primaryConcern || 'General Checkup'}</div>
              </div>
              <div style={{ width: '50%', boxSizing: 'border-box', paddingLeft: 24 }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>Identified Focus</div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: overall.color }}>{COND_LABELS[top?.[0]] || 'None'}</div>
              </div>
            </div>
          </div>
        </div>

        <SectionKicker title="Clinical Evaluation" marginBottom={pageOneLayout.kickerMarginBottom} />

        {analysisData.overallAssessment && (
          <div style={{ display: 'flex', marginBottom: pageOneLayout.summarySectionMarginBottom }}>
            <div style={{ flex: 1, marginRight: 48 }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 16 }}>Physician's Summary</div>
              <div style={{ fontFamily: FONTS.serif, fontSize: pageOneLayout.summaryFontSize, color: '#1A1A1A', lineHeight: pageOneLayout.summaryLineHeight }}>
                "{summaryText}"
              </div>
            </div>
            
            <div style={{ width: 1, backgroundColor: '#E5E5E5', marginRight: 48 }} />

            <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>Severity Index</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: pageOneLayout.severityFontSize, color: overall.color, fontWeight: 300, lineHeight: '1em', marginBottom: 8, letterSpacing: -1 }}>
                {analysisData.overallAssessment.severity || 'Moderate'}
              </div>
              {analysisData.overallAssessment.skinAge && (
                <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: '#737373' }}>
                  Estimated Skin Age: {analysisData.overallAssessment.skinAge}
                </div>
              )}
            </div>
          </div>
        )}

        {notedConcerns.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginRight: 12, marginBottom: pageOneLayout.concernLabelMarginBottom }}>Noted Conditions:</span>
            {notedConcerns.map((c, i) => (
              <span key={i} style={{ fontFamily: FONTS.sans, fontSize: pageOneLayout.concernChipFontSize, color: '#1A1A1A', border: '1px solid #E5E5E5', padding: pageOneLayout.concernChipPadding, borderRadius: 20, marginRight: 8, marginBottom: pageOneLayout.concernChipMarginBottom, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, minHeight: pageOneLayout.concernChipFontSize <= 9 ? 20 : 22, boxSizing: 'border-box', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{c}</span>
            ))}
          </div>
        )}

      </PageFrame>

      {/* ============ PAGE 2: DETAILED ANALYSIS ============ */}
      <PageFrame page={2} reportId={reportId} date={date}>
        <SectionKicker title="Diagnostic Mapping" subtitle="Detailed breakdown of detected skin conditions and their respective severity." />

        <div style={{ display: 'flex', marginBottom: 48, height: 440 }}>
          <div style={{ width: 300, flexShrink: 0, padding: 8, border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', marginRight: 48 }}>
            <div style={{ 
              flex: 1, 
              backgroundColor: '#FAFAF9', position: 'relative', overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {(annotatedImage || capturedImage?.dataUrl) ? (
                <img
                  src={annotatedImage || capturedImage?.dataUrl}
                  alt="Annotated skin mapping"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    flexShrink: 0,
                    filter: 'contrast(1.05)'
                  }}
                  crossOrigin="anonymous"
                />
              ) : null}
              <div style={{ position: 'absolute', bottom: 12, left: 12, backgroundColor: '#FFFFFF', padding: '6px 12px', fontFamily: FONTS.sans, fontSize: 9, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 1.5, border: '1px solid #E5E5E5' }}>
                AI Diagnostics
              </div>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {conds.slice(0, 6).map(([k, v]) => (
              <ConditionBar key={k} label={COND_LABELS[k]} percentage={v.percentage} severity={v.severity} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1 }}>
          {conds.slice(0, 3).map(([k, v], i) => {
            const s = getSeverity(v.severity);
            return (
              <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 24, borderTop: `2px solid ${s.dot}`, marginRight: i < 2 ? 24 : 0 }}>
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>Primary Finding 0{i + 1}</div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 20, color: '#1A1A1A', lineHeight: '1.2em', marginBottom: 12 }}>{COND_LABELS[k]}</div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: '#737373', lineHeight: '1.6em', flex: 1 }}>{v.description?.slice(0, 110)}...</div>
              </div>
            );
          })}
        </div>
      </PageFrame>

      {/* ============ PAGE 3: RECOMMENDATIONS ============ */}
      <PageFrame page={3} reportId={reportId} date={date}>
        <SectionKicker title="Prescribed Regimen" subtitle="A curated protocol designed to address the identified conditions." />

        <div style={{ display: 'flex', marginBottom: 48, flex: 1 }}>
          <RoutineColumn title="Morning Protocol" steps={am} />
          <div style={{ width: 1, backgroundColor: '#E5E5E5', marginLeft: 48, marginRight: 48 }} />
          <RoutineColumn title="Evening Protocol" steps={pm} />
        </div>

        {analysisData.recommendations?.followup && (
          <div style={{ marginBottom: 48 }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 16 }}>Follow-up Directives</div>
            <div style={{ padding: 24, backgroundColor: '#FAFAF9', border: '1px solid #E5E5E5' }}>
              <div style={{ fontFamily: FONTS.serif, fontSize: 15, color: '#1A1A1A', lineHeight: '1.6em' }}>
                {analysisData.recommendations.followup}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 'auto' }}>
          <div style={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 9, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>Attending Specialist</div>
              <div style={{ fontFamily: FONTS.serif, fontSize: 32, lineHeight: '1.1em', marginBottom: 8 }}>Dr. Medical</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, opacity: 0.9, letterSpacing: 0.5 }}>my-skin.ai Dermatology</div>
            </div>
            
            <div style={{ width: 1, height: 60, backgroundColor: 'rgba(255,255,255,0.2)' }} />
            
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 9, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>Contact</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 20, fontWeight: 300, letterSpacing: 1, marginBottom: 8 }}>hello@my-skin.ai</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, opacity: 0.9 }}>Digital Clinic</div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '0 24px 6px' }}>
          <div style={{ display: 'inline-block', maxWidth: '100%', fontFamily: FONTS.sans, fontSize: 9, color: '#A3A3A3', lineHeight: '1.6em', paddingBottom: 2 }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: 1 }}>Disclaimer:</span> {analysisData.disclaimer || "This AI-powered analysis is for informational purposes only and does not replace professional medical advice. Please consult a board-certified dermatologist for diagnosis and treatment."}
          </div>
        </div>
      </PageFrame>
    </div>
  );
});

PDFPreview.displayName = 'PDFPreview';

export default PDFPreview;
